'use client';
import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';

const credentials = [
  { icon: '🏫', text: '20 Years School Management Experience' },
  { icon: '💚', text: '10+ Years Health & Mental Wellness Coach' },
  { icon: '🧠', text: 'Clinical Hypnotherapist' },
  { icon: '⚡', text: 'Quantum Karmic Healer' },
  { icon: '🌸', text: 'Reiki Master' },
  { icon: '🔮', text: 'Past Life Regression Therapist' },
  { icon: '📖', text: 'Akashic Record Reader' },
  { icon: '👨‍👩‍👧', text: 'Family Constellation Facilitator' },
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="about"
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0d0818 0%, #130a24 40%, #1a0d30 70%, #0d0818 100%)' }}
    >
      {/* Lotus pattern */}
      <div className="absolute inset-0 lotus-bg opacity-40 pointer-events-none" />
      {/* Glow orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 glow-orb-gold" style={{ width: '500px', height: '500px', opacity: 0.35 }} />
        <div className="absolute left-0 bottom-0 glow-orb-lavender" style={{ width: '400px', height: '400px', opacity: 0.45 }} />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
            <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">About the Founder</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
          </div>
          <h2
            className="font-serif text-4xl md:text-5xl font-bold"
            style={{
              background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Shruti Saxena
          </h2>
          <p
            className="font-serif text-xl italic mt-2"
            style={{
              background: 'linear-gradient(135deg, #9b7fe8 0%, #b8a0e8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Founder, Shreevidha Healing Hub
          </p>
        </div>

        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Left: Photo */}
          <div className="flex flex-col items-center gap-8">
            <div className="relative">
              <div
                className="absolute -inset-4 rounded-full border border-[#c9a84c]/25"
                style={{ animation: 'rotateSlow 25s linear infinite' }}
              />
              <div
                className="absolute -inset-8 rounded-full border border-[#9b7fe8]/15"
                style={{ animation: 'rotateSlow 40s linear infinite reverse' }}
              />
              <div
                className="absolute inset-0 rounded-2xl"
                style={{ boxShadow: '0 0 60px rgba(124,92,191,0.35), 0 0 30px rgba(201,168,76,0.20)' }}
              />
              <div
                className="relative rounded-2xl overflow-hidden"
                style={{ border: '2px solid rgba(201,168,76,0.4)', width: '320px', height: '400px' }}
              >
                <Image
                  src="/assets/images/WhatsApp_Image_2026-05-10_at_7.03.12_PM-1778607646144.jpeg"
                  alt="Shruti Saxena – Founder of Shreevidha Healing Hub, Spiritual Healer and Wellness Coach"
                  fill
                  className="object-cover object-top"
                  priority
                />
              </div>
            </div>

            {/* Book Discovery Call CTA */}
            <a
              href="https://cal.com/shruti-g6cvvv/30min?overlayCalendar=true"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{
                background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 50%, #c9a84c 100%)',
                color: '#0d0818',
                boxShadow: '0 4px 30px rgba(201,168,76,0.35)',
              }}
            >
              ✨ Book Your Discovery Call
            </a>
          </div>

          {/* Right: Bio Content */}
          <div className="space-y-6">
            {/* Intro */}
            <div
              className="rounded-2xl p-6"
              style={{
                background: 'rgba(22, 12, 40, 0.80)',
                border: '1px solid rgba(201,168,76,0.22)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              }}
            >
              <p className="text-[#e8dff5]/85 text-sm leading-relaxed">
                Shruti Saxena is the founder of <span className="text-[#c9a84c] font-semibold">Shreevidha Healing Hub</span> — a sacred space dedicated to helping people heal emotionally, spiritually, mentally, and energetically. With over <span className="text-[#c9a84c] font-semibold">20 years of school management experience</span> and <span className="text-[#c9a84c] font-semibold">10+ years as a Health & Mental Wellness Coach</span>, she brings a rare combination of grounded wisdom and deep spiritual insight to every healing session.
              </p>
            </div>

            <p className="text-[#c4b5fd]/70 text-sm leading-relaxed">
              As a Clinical Hypnotherapist, Quantum Karmic Healer, Reiki Master, Past Life Regression Therapist, Akashic Record Reader, and Family Constellation Facilitator, Shruti offers a comprehensive and deeply transformative healing experience that addresses the root cause of pain, patterns, and blocks at every level.
            </p>

            {/* Credentials */}
            <div>
              <h3
                className="font-serif text-lg font-semibold mb-4"
                style={{
                  background: 'linear-gradient(135deg, #c9a84c, #e8c96a)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Qualifications & Expertise
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {credentials?.map((cred) => (
                  <div
                    key={cred?.text}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#e8dff5]/80 transition-all duration-200 hover:text-[#c9a84c]"
                    style={{
                      background: 'rgba(22, 12, 40, 0.65)',
                      border: '1px solid rgba(155,127,232,0.18)',
                    }}
                  >
                    <span className="text-base flex-shrink-0">{cred?.icon}</span>
                    <span className="text-xs">{cred?.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <div
              className="rounded-2xl p-6 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(124,92,191,0.10) 100%)',
                border: '1px solid rgba(201,168,76,0.28)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              }}
            >
              <div className="text-[#c9a84c] text-3xl mb-2 font-serif">&ldquo;</div>
              <p className="font-serif text-lg italic mb-3 text-[#e8dff5]">
                Healing begins the moment you choose yourself.
              </p>
              <p className="text-[#c9a84c]/80 text-sm tracking-widest">— Shruti Saxena ✨</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
